import React, { useEffect, useMemo, useState } from 'react';

function formatIsoDate(date) {
  try {
    return new Date(date).toISOString().slice(0, 10);
  } catch (err) {
    return new Date().toISOString().slice(0, 10);
  }
}

function applyReplacements(markdown, { date, companyAddress, contactEmail }) {
  return markdown
    .replaceAll('{{DATE}}', date)
    .replaceAll('{{COMPANY_ADDRESS}}', companyAddress)
    .replaceAll('{{CONTACT_EMAIL}}', contactEmail);
}

function parseInline(text) {
  const nodes = [];
  let rest = text || '';
  let key = 0;

  const pushText = (value) => {
    if (!value) return;
    nodes.push(<React.Fragment key={`t-${key++}`}>{value}</React.Fragment>);
  };

  while (rest.length) {
    const boldIndex = rest.indexOf('**');
    const linkIndex = rest.indexOf('[');

    const nextIndexCandidates = [boldIndex, linkIndex].filter((i) => i !== -1);
    if (!nextIndexCandidates.length) {
      pushText(rest);
      break;
    }

    const nextIndex = Math.min(...nextIndexCandidates);
    pushText(rest.slice(0, nextIndex));
    rest = rest.slice(nextIndex);

    if (rest.startsWith('**')) {
      const end = rest.indexOf('**', 2);
      if (end === -1) {
        pushText(rest);
        break;
      }
      const value = rest.slice(2, end);
      nodes.push(<strong key={`b-${key++}`}>{value}</strong>);
      rest = rest.slice(end + 2);
      continue;
    }

    if (rest.startsWith('[')) {
      const closeBracket = rest.indexOf(']');
      const openParen = closeBracket === -1 ? -1 : rest.indexOf('(', closeBracket);
      const closeParen = openParen === -1 ? -1 : rest.indexOf(')', openParen);
      if (closeBracket === -1 || openParen === -1 || closeParen === -1) {
        pushText(rest);
        break;
      }
      const label = rest.slice(1, closeBracket);
      const href = rest.slice(openParen + 1, closeParen);
      nodes.push(
        <a key={`a-${key++}`} href={href} rel="noreferrer">
          {label}
        </a>
      );
      rest = rest.slice(closeParen + 1);
      continue;
    }
  }

  return nodes;
}

function renderBlock(block, key) {
  const trimmed = block.trim();
  if (!trimmed) return null;

  if (trimmed === '---') {
    return <hr key={key} className="public-hr" />;
  }

  const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
  if (headingMatch) {
    const level = headingMatch[1].length;
    const content = parseInline(headingMatch[2].trim());
    if (level === 1) return <h1 key={key}>{content}</h1>;
    if (level === 2) return <h2 key={key}>{content}</h2>;
    return <h3 key={key}>{content}</h3>;
  }

  const lines = trimmed.split(/\r?\n/).filter((line) => line.trim().length);
  if (lines.length && lines.every((line) => /^-\s+/.test(line.trim()))) {
    return (
      <ul key={key}>
        {lines.map((line, idx) => (
          <li key={`${key}-li-${idx}`}>{parseInline(line.trim().replace(/^-+\s+/, ''))}</li>
        ))}
      </ul>
    );
  }

  if (/^https?:\/\/\S+$/.test(trimmed)) {
    return (
      <p key={key}>
        <a href={trimmed} rel="noreferrer">
          {trimmed}
        </a>
      </p>
    );
  }

  return (
    <p key={key}>
      {lines.map((line, idx) => (
        <React.Fragment key={`${key}-p-${idx}`}>
          {parseInline(line.trim())}
          {idx < lines.length - 1 ? <br /> : null}
        </React.Fragment>
      ))}
    </p>
  );
}

export default function PublicMarkdownPage({ src, title }) {
  const [markdown, setMarkdown] = useState('');
  const [error, setError] = useState(null);

  const replacements = useMemo(() => {
    const date = formatIsoDate(process.env.REACT_APP_LEGAL_LAST_UPDATED || new Date());
    const companyAddress = process.env.REACT_APP_COMPANY_ADDRESS || '';
    const contactEmail = process.env.REACT_APP_CONTACT_EMAIL || '';
    return { date, companyAddress, contactEmail };
  }, []);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setError(null);
        const response = await fetch(src, { cache: 'no-store' });
        if (!response.ok) {
          throw new Error(`Failed to load ${src} (${response.status})`);
        }
        const text = await response.text();
        if (!active) return;
        setMarkdown(applyReplacements(text, replacements));
      } catch (err) {
        if (!active) return;
        setError(err);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [src, replacements]);

  const blocks = useMemo(() => markdown.split(/\r?\n\r?\n/), [markdown]);

  return (
    <main className="public-page" aria-label={title}>
      {error ? (
        <>
          <h1>{title}</h1>
          <p>Unable to load this page right now.</p>
        </>
      ) : (
        blocks.map((block, idx) => renderBlock(block, `b-${idx}`))
      )}
    </main>
  );
}

